//frontend\src\config\seo.config.js
// SEO Configuration for Vet Assist Website

const normalizeBaseUrl = (url, fallback) => {
  const value = (url || fallback || "").trim();
  return value.replace(/\/+$/, "");
};

const getRuntimeBaseUrl = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return "";
};

const deriveBackendBaseUrl = (apiUrl) => {
  if (!apiUrl) return null;
  return apiUrl.trim().replace(/\/+$/, "");
};

const isLocalOrigin = (url = "") => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return ["localhost", "127.0.0.1", "::1"].includes(parsed.hostname);
  } catch {
    return false;
  }
};

const resolvePublicBaseUrl = (envUrl, runtimeUrl, fallback = "") => {
  const normalizedEnvUrl = normalizeBaseUrl(envUrl, "");
  const normalizedRuntimeUrl = normalizeBaseUrl(runtimeUrl, "");

  if (normalizedEnvUrl) {
    if (
      isLocalOrigin(normalizedEnvUrl) &&
      normalizedRuntimeUrl &&
      !isLocalOrigin(normalizedRuntimeUrl)
    ) {
      return normalizedRuntimeUrl;
    }

    return normalizedEnvUrl;
  }

  if (normalizedRuntimeUrl) {
    return normalizedRuntimeUrl;
  }

  return normalizeBaseUrl(fallback, "");
};

const deriveApiBaseFromRuntime = (runtimeUrl = "") => {
  const normalizedRuntimeUrl = normalizeBaseUrl(runtimeUrl, "");
  if (!normalizedRuntimeUrl) return "";

  try {
    const parsed = new URL(normalizedRuntimeUrl);
    if (isLocalOrigin(parsed.origin)) return "";

    // Runtime fallback assumes same-host API exposed under /api.
    return `${parsed.origin}/api`;
  } catch {
    return "";
  }
};

const resolveShareBaseUrl = (envUrl, runtimeUrl, fallback = "http://localhost:5000") => {
  const normalizedEnvUrl = normalizeBaseUrl(envUrl, "");
  const normalizedRuntimeUrl = normalizeBaseUrl(runtimeUrl, "");
  const runtimeApiBaseUrl = deriveApiBaseFromRuntime(normalizedRuntimeUrl);

  if (normalizedEnvUrl) {
    if (
      isLocalOrigin(normalizedEnvUrl) &&
      normalizedRuntimeUrl &&
      !isLocalOrigin(normalizedRuntimeUrl)
    ) {
      return runtimeApiBaseUrl || normalizedRuntimeUrl;
    }

    return normalizedEnvUrl;
  }

  if (normalizedRuntimeUrl && !isLocalOrigin(normalizedRuntimeUrl)) {
    return runtimeApiBaseUrl || normalizedRuntimeUrl;
  }

  return normalizeBaseUrl(fallback, "http://localhost:5000");
};

const normalizePathname = (path = "/") => {
  const rawValue = String(path || "").trim();

  if (!rawValue) return "/";

  if (/^https?:\/\//i.test(rawValue)) {
    try {
      const parsed = new URL(rawValue);
      const absolutePath = parsed.pathname || "/";
      return normalizePathname(absolutePath);
    } catch {
      // Fall through to plain pathname normalization for malformed URLs.
    }
  }

  const [cleanPath] = rawValue.split(/[?#]/);
  const trimmedPath = cleanPath.trim();

  if (!trimmedPath || trimmedPath === "/") return "/";
  return `/${trimmedPath.replace(/^\/+|\/+$/g, "")}`;
};

const runtimeBaseUrl = normalizeBaseUrl(getRuntimeBaseUrl(), "");
const BASE_URL = resolvePublicBaseUrl(import.meta.env.VITE_SITE_URL, runtimeBaseUrl);
const envBackendBase = normalizeBaseUrl(
  import.meta.env.VITE_SHARE_BASE_URL ||
    import.meta.env.VITE_BACKEND_URL ||
    deriveBackendBaseUrl(import.meta.env.VITE_API_URL),
  ""
);
const BACKEND_BASE_URL = resolveShareBaseUrl(envBackendBase, runtimeBaseUrl);
const SITE_NAME = "VetAssist";
const DEFAULT_IMAGE = BASE_URL ? `${BASE_URL}/URLlogo.webp` : "/URLlogo.webp";
const DEFAULT_DESCRIPTION =
  "VetAssist is veterinary software in the Philippines with an Admin Portal and Pet Owner Portal for integrated clinic and client workflows.";

const STATIC_PAGE_SHARE_PATHS = {
  home: "/",
  about: "/about",
  services: "/services",
  resources: "/resources",
  contact: "/contact",
  demo: "/demo",
  careers: "/careers",
  jobs: "/careers",
  privacy: "/privacy",
  terms: "/terms",
};

const SHARE_PATH_BY_ROUTE = {
  "/": STATIC_PAGE_SHARE_PATHS.home,
  "/about": STATIC_PAGE_SHARE_PATHS.about,
  "/services": STATIC_PAGE_SHARE_PATHS.services,
  "/resources": STATIC_PAGE_SHARE_PATHS.resources,
  "/contact": STATIC_PAGE_SHARE_PATHS.contact,
  "/careers": STATIC_PAGE_SHARE_PATHS.careers,
  "/jobs": STATIC_PAGE_SHARE_PATHS.careers,
  "/job-hiring": STATIC_PAGE_SHARE_PATHS.careers,
  "/demo": STATIC_PAGE_SHARE_PATHS.demo,
  "/privacy": STATIC_PAGE_SHARE_PATHS.privacy,
  "/terms": STATIC_PAGE_SHARE_PATHS.terms,
};

const PAGE_SCHEMA_TYPE_BY_KEY = {
  home: "WebPage",
  about: "AboutPage",
  services: "WebPage",
  resources: "CollectionPage",
  contact: "ContactPage",
  demo: "WebPage",
  jobs: "CollectionPage",
  privacy: "WebPage",
  terms: "WebPage",
};

export const seoConfig = {
  site: {
    name: SITE_NAME,
    url: BASE_URL,
    description: DEFAULT_DESCRIPTION,
    image: DEFAULT_IMAGE,
    twitter: "@vetassist",
    facebook: "vetassist",
    locale: "en_US",
  },

  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: BASE_URL,
    logo: DEFAULT_IMAGE,
    description: DEFAULT_DESCRIPTION,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      email: "support@vetassist.com",
    },
    sameAs: [
      "https://www.facebook.com/vetassist",
      "https://twitter.com/vetassist",
      "https://www.linkedin.com/showcase/vet-assist-ph",
    ],
  },

  pages: {
    home: {
      title: "VetAssist | Veterinary Clinic Business Management System for Vets, Booking, Pets, Appointment, Owner, Software,Records & Portal",
      description: "VetAssist VETerinary Ai software for veterinary business owners: medical records inventory, appointment reminders, and billing for clinic management; Pet Owner Portal for online booking.",
      keywords:
        "veterinary software, VetAssist, integrated veterinary Business management, vetassist.app, Admin Portal, clinic management software, veterinary EMR, scheduling, Pet Owner Portal, online vet appointment booking, pet medical records online",
      image: DEFAULT_IMAGE,
      imageAlt:
        "VetAssist veterinary software preview with Admin Portal and Pet Owner Portal interfaces.",
    },
    about: {
      title: "About Vet Assist | Our Mission & Team",
      description:
        "Learn about Vet Assist's mission to revolutionize veterinary practice management with innovative, user-friendly software solutions.",
      keywords: "about vet assist, veterinary software company, vet tech solutions",
      image: DEFAULT_IMAGE,
    },
    services: {
      title: "Services | Vet Assist Features & Solutions",
      description:
        "Discover Vet Assist's comprehensive features: appointment scheduling, medical records, inventory management, billing, and more.",
      keywords: "veterinary services, practice management features, vet software solutions",
      image: DEFAULT_IMAGE,
    },
    resources: {
      title: "Resources | Vet Assist Blog & Guides",
      description:
        "Expert guides, tips, and resources for veterinary professionals. Learn best practices for practice management and animal care.",
      keywords: "veterinary resources, vet practice guides, animal care tips, veterinary blog",
      image: DEFAULT_IMAGE,
    },
    contact: {
      title: "Contact Us | Get in Touch with Vet Assist",
      description:
        "Have questions? Contact Vet Assist for demos, support, or inquiries. We're here to help streamline your veterinary practice.",
      keywords: "contact vet assist, veterinary software support, request demo",
      image: DEFAULT_IMAGE,
    },
    demo: {
      title: "Request a Demo | See Vet Assist in Action",
      description:
        "Schedule a personalized demo of Vet Assist and discover how our software can transform your veterinary practice.",
      keywords: "vet assist demo, software demonstration, veterinary practice management demo",
      image: DEFAULT_IMAGE,
    },
    jobs: {
      title: "Careers | Join the Vet Assist Team",
      description:
        "Explore career opportunities at Vet Assist. Join our team and help revolutionize veterinary practice management.",
      keywords: "vet assist careers, veterinary software jobs, tech jobs",
      image: DEFAULT_IMAGE,
    },
    privacy: {
      title: "Privacy Policy | Vet Assist",
      description:
        "Read Vet Assist's privacy policy to understand how we protect and handle your data.",
      keywords: "privacy policy, data protection, HIPAA compliance",
      image: DEFAULT_IMAGE,
    },
    terms: {
      title: "Terms of Service | Vet Assist",
      description: "Review Vet Assist's terms of service and user agreement.",
      keywords: "terms of service, user agreement, legal terms",
      image: DEFAULT_IMAGE,
    },
  },
};

/**
 * Generate canonical URL
 * @param {string} path - URL path
 * @returns {string} - Full canonical URL
 */
export const getCanonicalUrl = (path = "") => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${BASE_URL}${cleanPath}`;
};

/**
 * Generate backend share preview URL
 * @param {string} path - URL path (e.g., /resources/my-post)
 * @returns {string} - Full preview URL
 */
export const getSharePreviewUrl = (path = "") => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${BACKEND_BASE_URL}${cleanPath}`;
};

/**
 * Generate dynamic backend share preview URL for any frontend path
 * @param {string} path - Frontend route path
 * @returns {string} - Full dynamic preview URL
 */
export const getDynamicSharePreviewUrl = (path = "/") => {
  const normalizedPath = normalizePathname(path);
  return `${BACKEND_BASE_URL}/share?path=${encodeURIComponent(normalizedPath)}`;
};

/**
 * Generate backend share preview URL from known page key
 * @param {string} pageKey - Key from seoConfig.pages
 * @returns {string} - Full preview URL
 */
export const getStaticPageSharePreviewUrl = (pageKey = "home") => {
  const sharePath = STATIC_PAGE_SHARE_PATHS[pageKey] || STATIC_PAGE_SHARE_PATHS.home;
  return getDynamicSharePreviewUrl(sharePath);
};

/**
 * Generate backend share preview URL from a frontend route path
 * @param {string} path - Frontend route path (e.g., /about or /resources/my-post)
 * @returns {string} - Full preview URL
 */
export const getSharePreviewUrlForPath = (path = "/") => {
  const normalizedPath = normalizePathname(path);
  const resourceSlugMatch = normalizedPath.match(/^\/resources\/([^/]+)$/);

  if (resourceSlugMatch?.[1]) {
    return `${BACKEND_BASE_URL}/resources/${encodeURIComponent(resourceSlugMatch[1])}`;
  }

  const sharePath = SHARE_PATH_BY_ROUTE[normalizedPath] || normalizedPath;
  return getDynamicSharePreviewUrl(sharePath);
};

/**
 * Generate RSS feed URL served by the backend.
 * @returns {string} - Full RSS feed URL
 */
export const getRssFeedUrl = () => {
  return `${BACKEND_BASE_URL}/rss.xml`;
};

/**
 * Get page-specific SEO data
 * @param {string} pageKey - Key from seoConfig.pages
 * @returns {object} - Page SEO configuration
 */
export const getPageSEO = (pageKey) => {
  const page = seoConfig.pages[pageKey] || seoConfig.pages.home;
  return {
    ...page,
    siteName: seoConfig.site.name,
    siteUrl: seoConfig.site.url,
  };
};

/**
 * Generate Organization structured data
 * @returns {object} - JSON-LD schema
 */
export const generateOrganizationStructuredData = () => {
  return seoConfig.organization;
};

/**
 * Generate Article structured data
 * @param {object} article - Article data
 * @returns {object} - JSON-LD schema
 */
export const generateArticleStructuredData = (article) => {
  if (!article) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.excerpt || article.description,
    image: article.featured_image || article.image || DEFAULT_IMAGE,
    datePublished: article.created_at || article.publishedAt,
    dateModified: article.updated_at || article.modifiedAt,
    author: {
      "@type": "Person",
      name: article.author_name || article.author || "Vet Assist Team",
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: DEFAULT_IMAGE,
      },
    },
    inLanguage: "en-PH",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${BASE_URL}/resources/${article.slug}`,
    },
  };
};

/**
 * Generate Breadcrumb structured data
 * @param {array} items - Breadcrumb items [{name, url}]
 * @returns {object} - JSON-LD schema
 */
export const generateBreadcrumbStructuredData = (items) => {
  if (!items || items.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url ? `${BASE_URL}${item.url}` : undefined,
    })),
  };
};

/**
 * Generate WebSite structured data with search action
 * @returns {object} - JSON-LD schema
 */
export const generateWebSiteStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BASE_URL}/#website`,
    name: SITE_NAME,
    url: BASE_URL,
    inLanguage: "en-PH",
    description:
      "VetAssist is veterinary software with an Admin Portal and Pet Owner Portal for veterinary clinics and pet owners in the Philippines.",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${BASE_URL}/resources?search={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
};

/**
 * Generate SoftwareApplication structured data for VetAssist homepage
 * @returns {object} - JSON-LD schema
 */
export const generateSoftwareApplicationStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${BASE_URL}/#softwareapplication`,
    name: SITE_NAME,
    url: BASE_URL,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Veterinary Practice Management Software",
    operatingSystem: "Web",
    description:
      "VetAssist is veterinary software for veterinary clinics and pet owners in the Philippines, combining an Admin Portal and Pet Owner Portal for clinic management software, veterinary EMR, scheduling, online vet appointment booking, and pet medical records online.",
    audience: [
      {
        "@type": "BusinessAudience",
        name: "Veterinary clinics",
      },
      {
        "@type": "PeopleAudience",
        name: "Pet owners",
      },
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "PHP",
      availability: "https://schema.org/InStock",
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: BASE_URL,
    },
  };
};

/**
 * Generate page-level structured data for non-article pages
 * @param {string} pageKey - Key from seoConfig.pages
 * @param {string} path - Current route pathname
 * @param {object} overrides - Optional override title/description/image
 * @returns {object} - JSON-LD schema
 */
export const generatePageStructuredData = (pageKey = "home", path = "/", overrides = {}) => {
  const pageSEO = getPageSEO(pageKey);
  const canonicalUrl = getCanonicalUrl(path);

  return {
    "@context": "https://schema.org",
    "@type": PAGE_SCHEMA_TYPE_BY_KEY[pageKey] || "WebPage",
    "@id": `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: overrides.title || pageSEO.title,
    description: overrides.description || pageSEO.description,
    image: overrides.image || pageSEO.image || DEFAULT_IMAGE,
    inLanguage: "en-PH",
    isPartOf: {
      "@type": "WebSite",
      "@id": `${BASE_URL}/#website`,
      name: SITE_NAME,
      url: BASE_URL,
    },
  };
};

/**
 * Generate JobPosting structured data
 * @param {object} job - Job data
 * @returns {object} - JSON-LD schema
 */
export const generateJobPostingStructuredData = (job) => {
  if (!job) return null;

  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description,
    datePosted: job.posted_date || job.created_at,
    validThrough: job.valid_through || job.deadline,
    employmentType: job.employment_type || "FULL_TIME",
    hiringOrganization: {
      "@type": "Organization",
      name: SITE_NAME,
      sameAs: BASE_URL,
      logo: DEFAULT_IMAGE,
    },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: job.location || "Remote",
        addressCountry: job.country || "US",
      },
    },
    baseSalary: job.salary
      ? {
          "@type": "MonetaryAmount",
          currency: "USD",
          value: {
            "@type": "QuantitativeValue",
            value: job.salary,
            unitText: "YEAR",
          },
        }
      : undefined,
  };
};
